import { useState, useEffect } from "react";
import { fetchData } from "../../api";

// Base URL for Strapi-hosted media (matches the convention used across the app).
const MEDIA_BASE = process.env.NEXT_PUBLIC_STRAPI_URL || "https://api.bouwbedrijfgeluk.nl";

const mediaUrl = (media) => (media?.url ? `${MEDIA_BASE}${media.url}` : null);

/**
 * Team section for the "Over ons" page.
 *
 * Content is CMS-driven (Strapi). Two endpoints are used:
 *
 *  1. Single type  "team-section"   (populate=*)
 *       - title            (Text)        e.g. "Ons Team"
 *       - description       (Long text)   intro paragraph
 *       - groupPhoto        (Media)       full-team photo, rendered as the page
 *                                         header background (see Banner.jsx),
 *                                         not repeated in this section
 *
 *  2. Collection  "team-members"   (populate=*)
 *       - name        (Text, required)   employee name
 *       - role        (Text)             function/title, e.g. "Projectleider"
 *       - description  (Long text)        bio the owner writes about the worker
 *       - image       (Media)            portrait photo
 *       - order       (Number)           optional, sorts ascending
 *
 * The whole section renders nothing until at least a title or one member
 * exists, so the page is unchanged until the CMS is filled in.
 */
const Team = () => {
    const [section, setSection] = useState(null);
    const [members, setMembers] = useState([]);

    useEffect(() => {
        // Single type -> object (or {} on 404/error).
        fetchData("team-section", true).then((data) => setSection(data && !Array.isArray(data) ? data : null));
        // Collection -> array (or {} on 404/error).
        fetchData("team-members", true).then((data) => setMembers(Array.isArray(data) ? data : []));
    }, []);

    const sortedMembers = [...members].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    // Nothing to show yet — keep the page clean until the CMS is populated.
    // (The group photo lives in the page header — see Banner.jsx — so it is not repeated here.)
    if (!section?.title && sortedMembers.length === 0) {
        return null;
    }

    return (
        <section className="relative py-24 bg-gradient-to-b from-white via-gray-50 to-white overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(40,103,102,0.04)_1px,transparent_0)] bg-[length:40px_40px] pointer-events-none"></div>

            <div className="container mx-auto px-4 relative z-10">
                {/* Heading + intro */}
                <div className="text-center mb-16">
                    <h2 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-[#286766] via-[#27352C] to-[#286766] bg-clip-text text-transparent mb-6">
                        {section?.title || "Ons Team"}
                    </h2>
                    <div className="w-24 h-1 bg-gradient-to-r from-[#286766] to-[#27352C] mx-auto rounded-full mb-8"></div>
                    {section?.description && (
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto">{section.description}</p>
                    )}
                </div>

                {/* Member cards */}
                {sortedMembers.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-6xl mx-auto">
                        {sortedMembers.map((member, index) => {
                            const portrait = mediaUrl(member.image);
                            return (
                                <div
                                    key={member.id ?? index}
                                    className="group relative bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-gray-100 overflow-hidden flex flex-col transition-all duration-500 hover:shadow-2xl hover:-translate-y-1"
                                    style={{ animationDelay: `${index * 0.07}s`, animation: "fadeInUp 0.6s ease-out forwards" }}
                                >
                                    {/* Portrait */}
                                    <div className="relative w-full aspect-[4/5] bg-gradient-to-br from-[#286766]/15 to-[#27352C]/10 overflow-hidden">
                                        {portrait ? (
                                            <img
                                                src={portrait}
                                                alt={member.name}
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <svg className="w-20 h-20 text-[#286766]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>

                                    {/* Name, role, description */}
                                    <div className="flex flex-col flex-1 p-8">
                                        <h3 className="text-2xl font-bold text-[#286766] mb-1">{member.name}</h3>
                                        {member.role && (
                                            <p className="text-sm font-semibold uppercase tracking-wider text-[#27352C]/70 mb-4">
                                                {member.role}
                                            </p>
                                        )}
                                        {/* Description area — the owner writes a bio per worker here (via the CMS). */}
                                        <p className="text-gray-600 leading-relaxed whitespace-pre-line flex-1">
                                            {member.description}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
};

export default Team;
